import type { FC } from "react";

import { motion, useReducedMotion } from "framer-motion";

/** Per-state dot motion signature. Each live/transitional lifecycle state
 *  gets a distinct rhythm so status reads at a glance without color alone;
 *  `none` is a static dot. Collapsed to static under prefers-reduced-motion. */
export type HudPillSignature =
  | "pulse"
  | "breathe"
  | "queued"
  | "blink"
  | "none";

const SIGNATURES: Record<
  Exclude<HudPillSignature, "none">,
  { animate: Record<string, number[]>; duration: number }
> = {
  // Errored — hard, fast blink that demands attention
  blink: { animate: { opacity: [1, 0.12, 1] }, duration: 0.7 },
  // Hibernating — slow scale+opacity breathe: powering down but still alive
  breathe: {
    animate: { opacity: [1, 0.5, 1], scale: [1, 0.7, 1] },
    duration: 2.6,
  },
  // Running — steady 1.6s opacity pulse (the sanctioned live heartbeat)
  pulse: { animate: { opacity: [1, 0.3, 1] }, duration: 1.6 },
  // Resuming — quicker queued pulse: work coming back online
  queued: { animate: { opacity: [0.35, 1, 0.35] }, duration: 1.2 },
};

export type HudStatusTone =
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "neutral"
  | "retained";

interface ToneClasses {
  border: string;
  text: string;
  dot: string;
}

const TONES: Record<HudStatusTone, ToneClasses> = {
  danger: {
    border: "border-rose-500/40 dark:border-rose-400/40",
    dot: "bg-rose-500 dark:bg-rose-400",
    text: "text-rose-700 dark:text-rose-300",
  },
  info: {
    border: "border-sky-500/40 dark:border-sky-400/40",
    dot: "bg-sky-500 dark:bg-sky-400",
    text: "text-sky-700 dark:text-sky-300",
  },
  neutral: {
    border: "border-slate-400/60 dark:border-slate-600/60",
    dot: "bg-slate-500 dark:bg-slate-500",
    text: "text-slate-600 dark:text-slate-400",
  },
  // Soft-delete / lossy-retained lifecycle states — kept distinct from the
  // amber dormant/hibernated family so "retained" reads as a settled archival
  // resting state, not a warning.
  retained: {
    border: "border-violet-500/40 dark:border-violet-400/40",
    dot: "bg-violet-500 dark:bg-violet-400",
    text: "text-violet-700 dark:text-violet-300",
  },
  success: {
    border: "border-emerald-500/40 dark:border-emerald-400/40",
    dot: "bg-emerald-500 dark:bg-emerald-400",
    text: "text-emerald-700 dark:text-emerald-300",
  },
  warning: {
    border: "border-amber-500/40 dark:border-amber-400/40",
    dot: "bg-amber-500 dark:bg-amber-400",
    text: "text-amber-700 dark:text-amber-300",
  },
};

interface HudStatusPillProps {
  label: string;
  tone: HudStatusTone;
  /** Animate the leading dot with a soft opacity pulse.
   *  @deprecated prefer `signature="pulse"` — kept for existing callers. */
  pulse?: boolean;
  /** Per-state dot motion signature. Overrides `pulse` when set. */
  signature?: HudPillSignature;
  /** Hide the leading dot entirely (e.g. when the pill is purely textual). */
  hideDot?: boolean;
  className?: string;
}

/** Bordered-pill status indicator in the HUD-CRT dialect.
 *
 * Used for live/offline/queued/parked banners, connection status, task
 * health flags. Mirrors the StatusBadge convention in InstanceDrawerHeader
 * but is canonical and tone-driven. Compose with `pulse` for live signals.
 */
export const HudStatusPill: FC<HudStatusPillProps> = ({
  label,
  tone,
  pulse = false,
  signature,
  hideDot = false,
  className = "",
}) => {
  const classes = TONES[tone];
  const reduceMotion = useReducedMotion();
  const resolved: HudPillSignature = signature ?? (pulse ? "pulse" : "none");
  const spec = resolved === "none" ? null : SIGNATURES[resolved];
  const dotClass = `h-1.5 w-1.5 ${classes.dot}`;
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest ${classes.border} ${classes.text} ${className}`}
    >
      {!hideDot &&
        (spec && !reduceMotion ? (
          <motion.span
            aria-hidden
            animate={spec.animate}
            transition={{
              duration: spec.duration,
              ease: "easeInOut",
              repeat: Infinity,
            }}
            className={dotClass}
          />
        ) : (
          <span aria-hidden className={dotClass} />
        ))}
      <span>{label}</span>
    </span>
  );
};
